import { get } from 'lodash';

import RouterHistory from '@reverbdotcom/commons/src/cms/lib/router_history';
import { V3 } from '../../api_request';
import { RECENT_SEARCH, SALE_SUGGESTION_TYPE } from './site_search';
import { Paths } from '../../shared/url_helpers';
import Suggestion from './suggestion';

import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { shouldClientSideResolve } from '@reverbdotcom/commons/src/components/core_link';
import { IUser } from '@reverbdotcom/commons/src/components/user_context_provider';

import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import experiments from '@reverbdotcom/commons/src/experiments';

import {
  reverb_search_CompletionType as CompletionType,
} from '../../../schemas/rql';
import { parseURL } from '@reverbdotcom/commons/src/link_parser';
import { isExperimentEnabled } from '@reverbdotcom/commons/src/user_context_helpers';

const SELECT_INTERACTION = 'select';
const SUBMIT_INTERACTION = 'submit';

// These are here temporarily in order to help us determine if a user would see a product family page
// if they are in the SHOW_PRODUCT_FAMILY_PAGE_LINKS. When the experiment is called, we can delete this.
const PRODUCT_FAMILY_REDIRECTS = [
  'fender bronco',
  'epiphone es-335',
  'marshall jcm 900',
  'rickenbacker 360',
  'fender coronado',
  'epiphone es-339',
  'paul reed smith custom 24',
  'fender nocaster',
  'fender performer',
  'gibson flying v',
  'martin d-18',
  'taylor gs mini',
  'suzuki omnichord',
  'fender showster',
  'martin d-28',
  'vox ac-30',
  'fender collider',
  'epiphone explorer',
  'fender robben ford',
  'paul reed smith mccarty',
  'fender powercaster',
  'rickenbacker 4001',
  'tc electronic flashback',
  'fender mustang',
  'fender lead',
  'fender katana',
  'fender terry-1',
  'fender blues junior',
  'fender precision bass',
  'gibson l-5',
  'fender arrow',
  'tc electronic ditto looper',
  'fender \'72',
  'fender meteora',
  'fender jazz stratocaster',
  'ludwig jazz festival',
  'fender flame',
  'electro-harmonix big muff',
  'squier jazz bass',
  'gibson les paul',
  'novation launchkey',
  'jhs morning glory',
  'rickenbacker 330',
  'fender musicmaster',
  'fender jaguar strat',
  'fender stevens lj',
  'fender deluxe',
  'tc electronic hall of fame',
  'vox ac-10',
  'fender broadcaster',
  'fender musiclander',
  'fender bass vi',
  'ludwig black beauty',
  'fender stratocaster',
  'fender swinger',
  'fender toronado',
  'fender marauder',
  'fender offset special',
  'arturia keystep',
  'squier precision bass',
  'epiphone j-45',
  'paul reed smith silver sky',
  'akai mpc',
  'fender bassman',
  'paul reed smith custom 22',
  'fender jazzmaster',
  'fender starcaster',
  'fender sixty-six',
  'boss metal zone',
  'gibson j-45',
  'gibson explorer',
  'fender jaguar',
  'epiphone les paul',
  'marshall jcm 2000',
  'fender prodigy',
  'fender tc-90',
  'fender jazz bass',
  'mxr carbon copy',
  'fender bullet',
  'fender jag-stang',
  'fender \'51',
  'gibson es-339',
  'epiphone flying v',
  'proco rat',
  'vox ac-15',
  'fender duo-sonic',
  'gibson firebird',
  'roland juno',
  'marshall jcm 800',
  'fender electric xii',
  'fender strat tele',
  'gibson es-335',
  'epiphone firebird',
  'gibson sg',
  'fender brawler',
  'fender talon',
  'fender princeton',
  'rickenbacker 4003',
  'fender telecaster',
  'fender cyclone',
  'fender showmaster',
  'fender sergio vallin',
  'fender maverick',
  'ibanez tube screamer',
  'taylor baby taylor',
  'fender esquire',
  'fender super-sonic',
  'ludwig supraphonic',
  'fender esprit',
  'fender d\'aquisto',
  'fender twin',
  'squier bass vi',
  'epiphone sg',
  'ludwig acrolite',
];

interface ISearchInteractionEvent {
  user: IUser;
  position?: number;
  entityId?: number;
  interactionType?: string;
  context: {
    fullTextQuery?: string;
    type?: string;
    scope?: string;
    suggestions?: string[];
    query?: string;
  };
}

export default class ReverbSiteSearchController {
  static checkForRedirect({ query, url, trackingEvent, user }) {
    if (isExperimentEnabled(user, experiments.NEW_AUTODIRECTS)) {
      ReverbSiteSearchController.redirectTo(url, trackingEvent);
      return;
    }

    ReverbSiteSearchController.getRedirect(query)
      .then(resp =>
        ReverbSiteSearchController.redirectTo(resp.redirect || url, trackingEvent),
      ).fail((_error) => {
        ReverbSiteSearchController.redirectTo(url, trackingEvent);
      });
  }

  static getRedirect(query: string) {
    return V3.get(Paths.apiListingRedirects.expand({}), { query });
  }

  static navigateWindow(href: string): void {
    window.location.href = href;
  }

  static async redirectTo(url: string, trackingParams: ISearchInteractionEvent) {
    const clientSide = await shouldClientSideResolve(parseURL(url), RouterHistory.getCurrentLocation());

    const productFamilyRedirectEligible = PRODUCT_FAMILY_REDIRECTS.includes(trackingParams.context.query.toLowerCase());

    trackEvent({
      productFamilyRedirectEligible,
      eventName: MParticleEventName.SiteSearch,
      position: trackingParams.position,
      suggestionId: String(trackingParams.entityId || ''),
      suggestionType: trackingParams.context.type,
      suggestionQuery: trackingParams.context.query,
      suggestionCount: get(trackingParams, 'context.suggestions.length', 0),
      interactionType: trackingParams.interactionType,
      targetUrl: url,
      experiments: formatExperimentsForEventAttributes(trackingParams.user, [
        experiments.DUMMY,
        experiments.SHOW_PRODUCT_FAMILY_PAGE_LINKS,
      ]),
    });

    if (clientSide) {
      RouterHistory.push(url);
    } else {
      ReverbSiteSearchController.navigateWindow(url);
    }
  }

  static selectSuggestion(user: IUser, suggestion: Suggestion, suggestions: Suggestion[], lastTypedValue = ''): void {
    if (suggestion.type === RECENT_SEARCH) {
      ReverbSiteSearchController.submitRecentQuery(user, suggestion, suggestions, lastTypedValue);
      return;
    }

    const trackingEvent: ISearchInteractionEvent = {
      user,
      interactionType: SELECT_INTERACTION,
      position: suggestion.position,
      entityId: get(suggestion, 'completion.id'),
      context: {
        fullTextQuery: suggestion.value,
        type: suggestion.type,
        query: lastTypedValue,
        scope: suggestion.context,
        suggestions: ReverbSiteSearchController.suggestionValues(suggestions),
      },
    };

    if (CompletionType[suggestion.type] || suggestion.type === SALE_SUGGESTION_TYPE) {
      ReverbSiteSearchController.redirectTo(suggestion.url, trackingEvent);
      return;
    }

    const uri = new URL(suggestion.url);
    const hostlessUrl = suggestion.url.replace(uri.origin, '');

    if (suggestion.context) {
      ReverbSiteSearchController.redirectTo(hostlessUrl, trackingEvent);
    } else {
      ReverbSiteSearchController.checkForRedirect({
        query: suggestion.value,
        url: hostlessUrl,
        trackingEvent,
        user,
      });
    }
  }

  private static submitRecentQuery(user: IUser, suggestion: Suggestion, suggestions: Suggestion[], lastTypedValue = ''): void {
    const query = suggestion.value;
    const path = Paths.marketplace.expand({ query });

    const trackingEvent = {
      user,
      interactionType: SELECT_INTERACTION,
      position: suggestion.position,
      context: {
        fullTextQuery: query,
        type: RECENT_SEARCH,
        query: lastTypedValue,
        suggestions: ReverbSiteSearchController.suggestionValues(suggestions),
      },
    };

    ReverbSiteSearchController.checkForRedirect({
      query,
      url: path,
      trackingEvent,
      user,
    });
  }

  static submitQuery(user: IUser, query: string, suggestions: Suggestion[], lastTypedValue = '', addtlParams = {}): void {
    const queryParams = { query: query, ...addtlParams };
    const path = Paths.marketplace.expand(queryParams);

    const trackingEvent = {
      user,
      interactionType: SUBMIT_INTERACTION,
      context: {
        fullTextQuery: query,
        query: lastTypedValue,
        suggestions: ReverbSiteSearchController.suggestionValues(suggestions),
      },
    };

    ReverbSiteSearchController.checkForRedirect({
      query,
      url: path,
      trackingEvent,
      user,
    });
  }

  private static suggestionValues(suggestions: Suggestion[]): string[] {
    return suggestions ? suggestions.map(s => s.value) : [];
  }
}
